<template>
  <div class="gl-code-edit">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${uid}-code`"
      :label="t('glCodes.glCodes')"
      :errors="v$.code.$errors"
    >
      <div class="gl-code-edit-value-container">
        <GLCodeAutocomplete
          :id="inputId"
          v-model="internalValue"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :gl-codes="glCodes"
          :placeholder="t('glCodes.glCodes')"
        />

        <InlineRemoveButton
          :disabled="disabled"
          class="w-auto"
          @click="$emit('remove', internalValue)"
        />
      </div>
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';

import GLCodeAutocomplete from '@/components/glCode/GLCodeAutocomplete.vue';
import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';

const props = defineProps({
  modelValue: {
    type: GLCode,
    default: () => new GLCode(),
  },
  glCodes: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, glCodes, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', glCode: GLCode): void;
  (event: 'update:model-value', newVal: GLCode): void;
}>();

const { t } = useI18n();
const { uid } = useUid('gl-code-edit');

const internalValue: Ref<GLCode> = ref(new GLCode(modelValue.value));
const rules = {
  code: {
    required: required(t('glCodes.glCodes')),
  },
};

const { v$ } = useValidations(internalValue, rules);

watch(
  modelValue,
  (newVal: GLCode) => {
    internalValue.value = new GLCode(newVal);
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newValue: GLCode) => {
    if (modelValue.value.code !== newValue.code) {
      emit('update:model-value', newValue);
    }
  },
  { deep: true },
);
</script>

<style>
.gl-code-edit {
  display: flex;
  gap: var(--gap-1);

  .input-wrapper {
    flex: 1 0 auto;

    .gl-code-edit-value-container {
      display: flex;
      gap: var(--gap-1);
      align-items: center;

      & > :first-child {
        /* the input should grow to fill the container */
        flex: 1 0 auto;
      }
    }
  }
}
</style>
